(() => {
  if (window.class1ProductInitialized) return;
  window.class1ProductInitialized = true;
  const initialized = new WeakSet();
  const mediaFor = (gallery) => Array.from(gallery?.querySelectorAll('.thumbnail-list__item[data-target]') || []);

  function syncGallery(gallery) {
    if (!gallery) return;
    const items = mediaFor(gallery);
    const selected = items.find((item) => item.querySelector('button[aria-current="true"]')) || items[0];
    if (!selected) return;
    const index = items.indexOf(selected) + 1;
    const counter = gallery.querySelector('.slider-counter--current');
    if (counter) counter.textContent = String(index);
    const visibleCount = gallery.querySelector('[data-class1-gallery-count]');
    if (visibleCount) visibleCount.textContent = `${index} / ${items.length}`;
    const image = gallery.querySelector(`[data-media-id="${selected.dataset.target}"] .product__modal-opener--image img`);
    const status = gallery.querySelector('[id^="GalleryStatus"]');
    if (status) status.textContent = `${index} of ${items.length}${image?.alt ? `: ${image.alt}` : ''}`;
    const strip = selected.closest('.thumbnail-list');
    if (!strip.firstElementChild?.classList.contains('class1-filmstrip__ghost--lead')) {
      strip.prepend(...strip.querySelectorAll('.class1-filmstrip__ghost--lead'));
    }
    const itemBounds = selected.getBoundingClientRect();
    const stripBounds = strip.getBoundingClientRect();
    strip.scrollTo({ left: strip.scrollLeft + (itemBounds.left + itemBounds.right - stripBounds.left - stripBounds.right) / 2 });
  }

  function setupGallery(section) {
    const gallery = section.querySelector('media-gallery');
    if (!gallery || initialized.has(gallery)) return;
    initialized.add(gallery);
    const filmstrip = gallery.querySelector('.class1-filmstrip');
    if (!filmstrip) return;
    const strip = filmstrip.querySelector('.thumbnail-list');
    const items = mediaFor(gallery);
    if (strip && items.length > 1) {
      const addGhost = (item, before) => {
        const image = item.querySelector('img')?.cloneNode();
        if (!image) return;
        image.removeAttribute('id');
        image.alt = '';
        const ghost = document.createElement('li');
        ghost.className = `class1-filmstrip__ghost${before ? ' class1-filmstrip__ghost--lead' : ''}`;
        ghost.setAttribute('aria-hidden', 'true');
        ghost.append(image);
        if (before) strip.prepend(ghost);
        else strip.append(ghost);
      };
      for (let i = 0; i < 3; i += 1) {
        addGhost(items[(items.length - 1 - i + items.length) % items.length], true);
        addGhost(items[i % items.length], false);
      }
    }

    gallery.querySelectorAll('[data-class1-filmstrip-step]').forEach((control) => {
      control.addEventListener('click', () => {
        const items = mediaFor(gallery);
        if (!items.length) return;
        const current = Math.max(0, items.findIndex((item) => item.querySelector('[aria-current="true"]')));
        const next = (current + Number(control.dataset.class1FilmstripStep) + items.length) % items.length;
        items[next].querySelector('button')?.click();
        syncGallery(gallery);
      });
    });
    filmstrip.addEventListener('click', (event) => {
      if (event.target.closest('.thumbnail-list__item button')) syncGallery(gallery);
    });
    filmstrip.addEventListener('keydown', (event) => {
      if (!event.target.closest('.thumbnail-list__item button') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const items = mediaFor(gallery);
      const current = items.findIndex((item) => item.contains(event.target));
      if (current < 0) return;
      const next = (current + (event.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length;
      const button = items[next].querySelector('button');
      button?.focus();
      button?.click();
      syncGallery(gallery);
    });
    gallery.querySelector('[id^="GalleryViewer"]')?.addEventListener('slideChanged', () => syncGallery(gallery));
    syncGallery(gallery);
  }

  function setupModal(section) {
    const sectionId = section.dataset.section;
    const modal = document.getElementById(`ProductModal-${sectionId}`);
    if (!modal || initialized.has(modal)) return;
    initialized.add(modal);
    const nav = document.createElement('div');
    nav.className = 'class1-product-modal__nav';
    nav.innerHTML = '<button type="button" data-step="-1" aria-label="Previous product media">&#8592;</button><span class="class1-product-modal__count" role="status"></span><button type="button" data-step="1" aria-label="Next product media">&#8594;</button>';
    modal.querySelector('.product-media-modal__dialog').append(nav);
    const content = modal.querySelector('.product-media-modal__content');
    const zoom = document.createElement('button');
    zoom.type = 'button';
    zoom.className = 'class1-product-modal__zoom';
    zoom.textContent = 'Zoom in';
    zoom.setAttribute('aria-label', 'Zoom in');
    zoom.setAttribute('aria-pressed', 'false');
    modal.querySelector('.product-media-modal__dialog').append(zoom);
    let zoomedImage;
    let originalSizes;
    const resetZoom = () => {
      if (zoomedImage) {
        zoomedImage.sizes = originalSizes;
        zoomedImage = null;
      }
      content.classList.remove('class1-product-modal__zoomed');
      content.style.removeProperty('--class1-zoom-width');
      content.scrollTo(0, 0);
      zoom.textContent = 'Zoom in';
      zoom.setAttribute('aria-label', 'Zoom in');
      zoom.setAttribute('aria-pressed', 'false');
    };
    const activeImage = () => content.querySelector('img.active[data-media-id]');
    zoom.addEventListener('pointerup', (event) => event.stopPropagation());
    zoom.addEventListener('click', (event) => {
      event.stopPropagation();
      const image = activeImage();
      if (!image) return;
      if (content.classList.contains('class1-product-modal__zoomed')) {
        resetZoom();
        return;
      }
      const width = Math.max(image.getBoundingClientRect().width * 2, Math.min(image.naturalWidth, window.innerWidth * 2));
      originalSizes = image.sizes;
      zoomedImage = image;
      image.sizes = `${Math.ceil(width)}px`;
      content.style.setProperty('--class1-zoom-width', `${width}px`);
      content.classList.add('class1-product-modal__zoomed');
      zoom.textContent = 'Zoom out';
      zoom.setAttribute('aria-label', 'Zoom out');
      zoom.setAttribute('aria-pressed', 'true');
      requestAnimationFrame(() => {
        content.scrollLeft = (content.scrollWidth - content.clientWidth) / 2;
        content.scrollTop = (content.scrollHeight - content.clientHeight) / 2;
      });
    });
    content.addEventListener('pointerup', (event) => {
      if (content.classList.contains('class1-product-modal__zoomed') || event.target.matches('img.active[data-media-id]')) event.stopPropagation();
    });
    content.addEventListener('click', (event) => {
      if (event.target.matches('img.active[data-media-id]')) zoom.click();
    });
    document.body.addEventListener('modalClosed', resetZoom);
    const media = () => Array.from(modal.querySelector('.product-media-modal__content').children).filter((node) => node.hasAttribute('data-media-id'));
    const updateCount = () => {
      const items = media();
      const index = items.findIndex((item) => item.classList.contains('active'));
      nav.querySelector('.class1-product-modal__count').textContent = `${Math.max(index + 1, 1)} / ${items.length}`;
      nav.hidden = items.length < 2;
      zoom.hidden = !(items[index] instanceof HTMLImageElement);
    };
    nav.addEventListener('pointerup', (event) => event.stopPropagation());
    nav.addEventListener('click', (event) => {
      const step = event.target.closest('button[data-step]');
      if (!step) return;
      const items = media();
      if (!items.length) return;
      const current = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
      resetZoom();
      const next = (current + Number(step.dataset.step) + items.length) % items.length;
      items[current].classList.remove('active');
      items[next].classList.add('active');
      if (window.pauseAllMedia) window.pauseAllMedia();
      const gallery = section.querySelector('media-gallery');
      const target = gallery?.querySelector(`.thumbnail-list__item[data-target="${sectionId}-${items[next].dataset.mediaId}"] button`);
      target?.click();
      syncGallery(gallery);
      updateCount();
    });
    modal.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.matches('input, textarea, select, video, model-viewer')) return;
      if (content.classList.contains('class1-product-modal__zoomed') && content.scrollWidth > content.clientWidth && !event.target.closest('.class1-product-modal__nav')) {
        event.preventDefault();
        content.scrollBy({ left: event.key === 'ArrowRight' ? 80 : -80, behavior: 'auto' });
        return;
      }
      event.preventDefault();
      nav.querySelector(`[data-step="${event.key === 'ArrowRight' ? '1' : '-1'}"]`)?.click();
    });
    modal.addEventListener('click', (event) => {
      if (event.target.closest('.product-media-modal__toggle')) return;
      requestAnimationFrame(updateCount);
    });
    section.querySelector('media-gallery')?.addEventListener('click', (event) => {
      if (event.target.closest('.product__media-toggle')) requestAnimationFrame(updateCount);
    });
    updateCount();
  }

  function setupProduct(section) {
    if (initialized.has(section)) return;
    initialized.add(section);
    const product = section.querySelector('[data-class1-product-section]');
    const sticky = section.querySelector('[data-class1-sticky]');
    if (!product || !sticky) return;
    setupGallery(section);
    setupModal(section);
    const mobile = window.matchMedia('(max-width: 749px)');
    let observer;
    let controlsVisible = true;
    const updateSticky = () => {
      const active = mobile.matches && !controlsVisible && product.dataset.class1State !== 'pending' && !window.Shopify?.designMode;
      sticky.dataset.class1Visible = active ? 'true' : 'false';
      sticky.setAttribute('aria-hidden', active ? 'false' : 'true');
      sticky.inert = !active;
      document.body.classList.toggle('class1-product-has-sticky', active);
    };
    const watchActions = () => {
      observer?.disconnect();
      controlsVisible = true;
      updateSticky();
      const target = product.querySelector(product.dataset.class1State === 'in_stock' ? '.class1-product__purchase' : '.class1-product__source');
      if (!target || !('IntersectionObserver' in window)) { controlsVisible = true; updateSticky(); return; }
      observer = new IntersectionObserver(([entry]) => {
        controlsVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
        updateSticky();
      }, { threshold: 0 });
      observer.observe(target);
    };
    mobile.addEventListener('change', updateSticky);
    section.addEventListener('submit', (event) => {
      if (product.dataset.class1State !== 'in_stock' && event.target.matches('[data-type="add-to-cart-form"]')) event.preventDefault();
    }, true);
    section.addEventListener('class1:variant-unavailable', () => {
      product.dataset.class1PriceOnRequest = 'true';
      const picker = product.querySelector('variant-selects');
      const selectedOptions = Array.from(picker?.querySelectorAll('input:checked, option:checked') || []);
      const productUrl = new URL(section.dataset.url, window.location.origin);
      const optionIds = selectedOptions.map((option) => option.dataset.optionValueId).filter(Boolean);
      if (optionIds.length) productUrl.searchParams.set('option_values', optionIds.join(','));
      const title = product.querySelector('.product__title h1')?.textContent.trim() || document.title;
      const variantTitle = selectedOptions.map((option) => option.value).join(' / ');
      const message = `Hello Class1 Watches, could you please share the price and details for ${title}${variantTitle ? ` — ${variantTitle}` : ''}? ${productUrl.href}`;
      const contactUrl = `https://wa.me/61447404343?text=${encodeURIComponent(message)}`;
      const enquiry = product.querySelector('.class1-product__enquiry');
      if (enquiry) {
        const enquiryUrl = new URL(enquiry.href);
        enquiryUrl.searchParams.set('product_url', productUrl.href);
        enquiry.href = enquiryUrl.href;
      }
      product.dataset.class1State = 'sourceable';
      const sourceAction = product.querySelector('.class1-product__source-action');
      if (sourceAction) sourceAction.textContent = 'Price on request';
      const contactLabel = product.querySelector('.class1-product__contact span');
      if (contactLabel) contactLabel.textContent = 'Price on request';
      product.querySelector('[data-class1-sku-row]')?.remove();
      product.querySelectorAll('.class1-product__source-action, .class1-product__contact').forEach((link) => { link.href = contactUrl; });
      sticky.querySelector('.class1-product__sticky-action').href = contactUrl;
      sticky.querySelector('.class1-product__sticky-price').textContent = 'Price on request';
      sticky.querySelector('.class1-product__sticky-action').textContent = 'Price on request';
      const schema = section.querySelector('[data-class1-product-schema]');
      if (schema) {
        const productSchema = JSON.parse(schema.textContent);
        delete productSchema.offers;
        delete productSchema.sku;
        delete productSchema.mpn;
        schema.textContent = JSON.stringify(productSchema);
      }
      watchActions();
    });
    watchActions();

    section.class1Update = (html) => {
      const source = html.querySelector(`[data-class1-product-section="${product.dataset.class1ProductSection}"]`);
      if (!source) return;
      product.dataset.class1State = source.dataset.class1State;
      product.dataset.class1PriceOnRequest = source.dataset.class1PriceOnRequest;
      const specifications = source.querySelector('[data-class1-specifications]');
      const currentSpecifications = product.querySelector('[data-class1-specifications]');
      if (specifications && currentSpecifications) currentSpecifications.innerHTML = specifications.innerHTML;
      const specificationDetails = source.querySelector('[data-class1-specification-details]');
      const currentSpecificationDetails = product.querySelector('[data-class1-specification-details]');
      if (specificationDetails && currentSpecificationDetails) currentSpecificationDetails.hidden = specificationDetails.hidden;
      for (const selector of ['.class1-product__source-action', '.class1-product__contact', '.class1-product__enquiry']) {
        const current = product.querySelector(selector);
        const incoming = source.querySelector(selector);
        if (current && incoming) {
          current.href = incoming.href;
          if (selector === '.class1-product__source-action') current.textContent = incoming.textContent;
          if (selector === '.class1-product__contact') {
            const label = current.querySelector('span');
            if (label) label.textContent = incoming.querySelector('span')?.textContent || '';
          }
        }
      }
      const incomingSticky = html.querySelector('[data-class1-sticky]');
      const currentStickyPrice = sticky.querySelector('.class1-product__sticky-price');
      if (currentStickyPrice) currentStickyPrice.textContent = incomingSticky?.querySelector('.class1-product__sticky-price')?.textContent || '';
      sticky.querySelector('.class1-product__sticky-action').textContent = incomingSticky?.querySelector('.class1-product__sticky-action')?.textContent || '';
      sticky.querySelector('.class1-product__sticky-action').href = incomingSticky?.querySelector('.class1-product__sticky-action')?.href || '';
      const schema = section.querySelector('[data-class1-product-schema]');
      const incomingSchema = html.querySelector('[data-class1-product-schema]');
      if (schema && incomingSchema) schema.textContent = incomingSchema.textContent;
      if (product.dataset.class1State !== 'in_stock') {
        const submit = product.querySelector('.product-form__submit');
        if (submit) submit.disabled = true;
      }
      watchActions();
      syncGallery(section.querySelector('media-gallery'));
    };
  }

  const init = () => {
    const sections = document.querySelectorAll('product-info[id^="MainProduct-"]');
    sections.forEach(setupProduct);
    if (sections.length) document.documentElement.classList.add('class1-product-ready');
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('product-info:loaded', (event) => setupProduct(event.target));
  document.addEventListener('shopify:section:load', init);
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
      const section = data?.event?.target?.closest('product-info[id^="MainProduct-"]');
      if (!section) return;
      const product = section.querySelector('[data-class1-product-section]');
      product.dataset.class1State = 'pending';
      section.querySelector('.product-form__submit')?.setAttribute('disabled', '');
      const sticky = section.querySelector('[data-class1-sticky]');
      sticky?.setAttribute('data-class1-visible', 'false');
      sticky?.setAttribute('aria-hidden', 'true');
      if (sticky) sticky.inert = true;
      document.body.classList.remove('class1-product-has-sticky');
    });
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const section = document.querySelector(`product-info[data-section="${data.sectionId}"]`);
      section?.class1Update?.(data.html);
    });
  }
})();
